import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, Users, DollarSign, Ticket, ImageOff } from "lucide-react";
import { eventosPublicosApi, EventoPublicoCatalogo } from "../../lib/api";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

function fmtFecha(s: string) {
  try {
    return new Date(s.slice(0, 10) + "T00:00:00").toLocaleDateString("es-VE", {
      weekday: "long", day: "2-digit", month: "long",
    });
  } catch { return s; }
}

function sucursalDeUrl(): number | undefined {
  const p = new URLSearchParams(window.location.search).get("sucursal");
  const n = Number(p);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

interface SucConfig { logoUrl?: string | null; imagenFondoUrl?: string | null; colorPrimario?: string | null; }

export default function EventosCatalogoPage() {
  const sucursalId = sucursalDeUrl();
  const [eventos, setEventos] = useState<EventoPublicoCatalogo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<SucConfig | null>(null);

  useEffect(() => {
    eventosPublicosApi.catalogo(sucursalId)
      .then(setEventos)
      .catch((e) => setError(e?.message ?? "No pudimos cargar los eventos"));
  }, [sucursalId]);

  // Si el link viene con ?sucursal=, vestimos la página con SU identidad
  // (fondo, favicon, título) -- igual que reservar/walkin/encuestas.
  useEffect(() => {
    if (!sucursalId) return;
    fetch("/api/v1/sucursales")
      .then((r) => r.json())
      .then((d: any[]) => {
        const s = Array.isArray(d) ? d.find((x) => x.id === sucursalId) : null;
        if (s?.nombre) aplicarTituloPagina(s.nombre);
      })
      .catch(() => {});
    fetch(`/api/v1/sucursales/${sucursalId}/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setConfig(d); aplicarFaviconPagina(d.logoUrl); } })
      .catch(() => {});
  }, [sucursalId]);

  const fondoEstilo: CSSProperties = config?.imagenFondoUrl
    ? {
        backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${config.imagenFondoUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }
    : {};

  return (
    <div style={{ minHeight: "100vh", background: "#F6F1E7", ["--accent" as any]: config?.colorPrimario || "#3A6B8C", ...fondoEstilo }}>
      <style>{STYLES}</style>
      <div className="evp-cat-wrap">
        <header className="evp-cat-head">
          <Ticket size={34} strokeWidth={1.6} />
          <h1>Eventos</h1>
          <p>Reservá tu entrada con anticipación — el cupo es limitado.</p>
        </header>

        {error && <div className="evp-cat-error">{error}</div>}

        {!eventos && !error && (
          <div className="evp-cat-empty">Cargando eventos…</div>
        )}

        {eventos && eventos.length === 0 && (
          <div className="evp-cat-empty">No hay eventos disponibles por ahora. ¡Volvé pronto!</div>
        )}

        <div className="evp-cat-grid">
          {eventos?.map((ev) => (
            <Link to={`/eventos/${ev.id}`} key={ev.id} className="evp-cat-card">
              <div className="evp-cat-img">
                {ev.imagenUrl ? (
                  <img src={ev.imagenUrl} alt={ev.titulo} loading="lazy" />
                ) : (
                  <div className="evp-cat-img-fallback"><ImageOff size={28} /></div>
                )}
                {ev.cuposDisponibles <= 5 && ev.cuposDisponibles > 0 && (
                  <span className="evp-cat-badge evp-cat-badge-amber">¡Últimos {ev.cuposDisponibles} cupos!</span>
                )}
                {ev.cuposDisponibles === 0 && (
                  <span className="evp-cat-badge evp-cat-badge-rose">Agotado</span>
                )}
              </div>
              <div className="evp-cat-body">
                <h2>{ev.titulo}</h2>
                <div className="evp-cat-meta">
                  <span><Calendar size={13} /> {fmtFecha(ev.fechaEvento)}</span>
                  {ev.horaInicio && <span><Clock size={13} /> {ev.horaInicio.slice(0, 5)}</span>}
                </div>
                <div className="evp-cat-foot">
                  <span className="evp-cat-precio"><DollarSign size={14} /> {Number(ev.precioUsd).toFixed(2)}</span>
                  <span className="evp-cat-cupos"><Users size={13} /> {ev.cuposDisponibles} disponibles</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const STYLES = `
.evp-cat-wrap { max-width: 1040px; margin: 0 auto; padding: 24px 16px 48px; }
.evp-cat-head { text-align: center; color: var(--accent, #3A6B8C); margin-bottom: 28px; }
.evp-cat-head h1 { font-family: Georgia, serif; font-size: 30px; font-weight: 700; color: #1c1c1c; margin: 8px 0 4px; }
.evp-cat-head p { color: #666; font-size: 14px; }
.evp-cat-error { background: #fee2e2; color: #991b1b; padding: 12px; border-radius: 10px; text-align: center; margin-bottom: 16px; }
.evp-cat-empty { text-align: center; color: #888; padding: 48px 16px; font-size: 15px; }
.evp-cat-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 18px; }
.evp-cat-card {
  background: #fff; border-radius: 16px; overflow: hidden; text-decoration: none; color: inherit;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06); border: 1px solid #ece6d8;
  transition: transform .15s ease, box-shadow .15s ease;
  display: flex; flex-direction: column;
}
.evp-cat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.1); }
.evp-cat-img { position: relative; width: 100%; height: 150px; background: #e9e4d8; }
@media (min-width: 640px) { .evp-cat-img { height: 180px; } }
.evp-cat-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.evp-cat-img-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #b5ac97; }
.evp-cat-badge {
  position: absolute; top: 10px; right: 10px; font-size: 11px; font-weight: 700;
  padding: 4px 10px; border-radius: 999px; color: #fff;
}
.evp-cat-badge-amber { background: #d97706; }
.evp-cat-badge-rose { background: #be123c; }
.evp-cat-body { padding: 14px 16px 16px; flex: 1; display: flex; flex-direction: column; }
.evp-cat-body h2 { font-size: 16px; font-weight: 700; color: #1c1c1c; margin: 0 0 8px; font-family: Georgia, serif; }
.evp-cat-meta { display: flex; flex-wrap: wrap; gap: 10px; font-size: 12.5px; color: #666; margin-bottom: 10px; }
.evp-cat-meta span { display: inline-flex; align-items: center; gap: 4px; }
.evp-cat-foot { margin-top: auto; display: flex; align-items: center; justify-content: space-between; }
.evp-cat-precio { display: inline-flex; align-items: center; gap: 2px; font-weight: 700; color: var(--accent, #3A6B8C); font-size: 15px; }
.evp-cat-cupos { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: #888; }
`;
